import type {Room} from 'matrix-js-sdk';

export interface RoomPreviewPrefetcherDeps {
    getRooms: () => Room[];
    hasPreview: (room: Room) => boolean;
    canPaginate: (room: Room) => boolean;
    wasOpened: (roomId: string) => boolean;
    paginate: (roomId: string, limit: number) => Promise<unknown>;
    isActive: () => boolean;
    onRoomPaginated: (roomId: string) => void;
}

export const PREVIEW_PREFETCH_LIMIT = 20;
export const PREVIEW_PREFETCH_CONCURRENCY = 3;

export class RoomPreviewPrefetcher {
    private readonly attempted = new Set<string>();
    private started = false;

    constructor(
        private readonly deps: RoomPreviewPrefetcherDeps,
        private readonly limit: number = PREVIEW_PREFETCH_LIMIT,
        private readonly concurrency: number = PREVIEW_PREFETCH_CONCURRENCY,
    ) {}

    async run(): Promise<void> {
        if (this.started) return;
        this.started = true;

        const queue = this.deps.getRooms().filter(room => this.isCandidate(room));
        const worker = async () => {
            while (this.deps.isActive()) {
                const room = queue.shift();
                if (!room) return;
                if (!this.isCandidate(room)) continue;
                this.attempted.add(room.roomId);
                try {
                    await this.deps.paginate(room.roomId, this.limit);
                } catch {
                    continue;
                }
                if (!this.deps.isActive()) return;
                this.deps.onRoomPaginated(room.roomId);
            }
        };

        await Promise.all(Array.from({length: this.concurrency}, worker));
    }

    private isCandidate(room: Room): boolean {
        if (this.attempted.has(room.roomId)) return false;
        if (room.getMyMembership() !== 'join' || room.isSpaceRoom()) return false;
        if (this.deps.wasOpened(room.roomId)) return false;
        if (this.deps.hasPreview(room)) return false;
        return this.deps.canPaginate(room);
    }
}
